// redux/healthcareSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { userRequest } from "../api/requestMethods";
import {
    MAIN,
    applyBranchEventToSections,
    branchEventIsFor,
} from "../utils/branchAvailability";

const initialState = {
    healthcareData: [],
    optionGroups: [],
    isFetching: false,
    error: null,
    selectedVendor: null,
    details: null,
    // The request id of the latest details read; an older answer is dropped.
    detailsReadId: null,
};

// ONE place that knows where a single pharmacy comes from.
//
// GET /healthcare/healthcare-vendor/:vendorId is PUBLIC and matches
// { $or: [{ _id }, { owner }] }, so it answers for BOTH id spaces a storefront
// URL can carry (the directory links with the owner id, the landing page links
// with the Healthcare document id).
//
// The list endpoint (/healthcare-vendors?vendorId=) is NOT an alternative: it
// restricts the vendorId scope to platform super admins and to the vendor
// itself, so an ordinary customer opening a shared link, refreshing, or coming
// back from a payment provider got 403 "Forbidden: healthcare vendor list
// scope" and the storefront had nothing to render.
//
// It names the branch (4 Oct 2026): `?outletId=` a HealthcareOutlet id, or
// "main" for the pharmacy itself, so every product comes back as it stands
// there (sold out or switched off there, the price there).
const requestHealthcareVendor = (vendorId, outletId = null) =>
    userRequest.get(`/healthcare/healthcare-vendor/${vendorId}`, {
        params: { outletId: outletId || MAIN },
    });

// ONE place that knows the id shape the rest of the app consumes.
//
// The directory list is grouped by owner, so an entry there carries
// `healthcareId` = the Healthcare document id (and `_id` = the group key, the
// owner). The single-vendor endpoint returns the document itself: `_id` = the
// document id, `owner` = the owner id, and no `healthcareId` at all.
//
// healthcareCartSlice keys off `healthcareId || _id`, so without this the two
// paths would resolve to different ids. Supplying `healthcareId` makes a cold
// load key exactly as the directory path does. `_id` is deliberately left as
// the document id rather than rewritten to the group key: nothing downstream
// reads it for the cart (the order screens read `owner || _id`, and `owner` is
// present on both shapes), and `healthcareDetails.vendor._id` is what GA4
// vendor_view already reports.
export const normalizeHealthcareVendor = (vendor) => {
    if (!vendor || typeof vendor !== "object") return vendor;
    return { ...vendor, healthcareId: vendor.healthcareId || vendor._id };
};

export const fetchHealthcareVendors = createAsyncThunk(
    "healthcare/fetchHealthcareVendors",
    async (_, { rejectWithValue }) => {
        try {
            const response = await userRequest.get("/healthcare/healthcare-vendors");
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to fetch healthcare vendors"
            );
        }
    }
);

// The storefront's single fetch: the pharmacy, its sections and its products.
// It populates BOTH `details` (what the page renders) and `selectedVendor`
// (what the cart slice is seeded from), so a cold load costs one request
// instead of two calls to the same endpoint.
// The argument is the storefront's vendor id, or { vendorId, outletId } for
// a branch.
export const fetchHealthcareVendorDetails = createAsyncThunk(
    "healthcare/fetchHealthcareVendorDetails",
    async (arg, { rejectWithValue }) => {
        const { vendorId, outletId = null } =
            arg && typeof arg === "object" ? arg : { vendorId: arg };
        try {
            const response = await requestHealthcareVendor(vendorId, outletId);
            const vendor = normalizeHealthcareVendor(response.data);
            return { vendorId, outletId: outletId || null, sections: vendor.sections, vendor };
        } catch (error) {
            // Optional chaining matters here: on a network failure there is no
            // `error.response`, and reading it threw before rejectWithValue
            // could run, which left `error` null and the page unable to tell a
            // failed load from a pharmacy it had simply not fetched yet.
            return rejectWithValue(
                error.response?.data || "Failed to fetch healthcare vendor"
            );
        }
    }
);

const healthcareSlice = createSlice({
    name: "healthcare",
    initialState,
    reducers: {
        resetOptionGroups: (state) => {
            state.optionGroups = [];
        },
        setSelectedVendor: (state, action) => {
            state.selectedVendor = normalizeHealthcareVendor(action.payload);
        },
        /* Realtime `branchProduct:changed` (4 Oct 2026): a pharmacy branch
           switched a product on or off, or sold it out. Patched in place on
           the open storefront. */
        applyBranchProductChange: (state, action) => {
            const payload = action.payload || {};
            const vendor = state.details?.vendor;
            const ids = [state.details?.vendorId, vendor?._id, vendor?.owner];
            if (!state.details || !branchEventIsFor(payload, ids)) return;
            applyBranchEventToSections(state.details.sections, payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchHealthcareVendors.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(fetchHealthcareVendors.fulfilled, (state, action) => {
                state.healthcareData = action.payload.healthcareVendors;
                state.isFetching = false;
                state.error = null;
            })
            .addCase(fetchHealthcareVendors.rejected, (state, action) => {
                state.error = action.payload;
                state.isFetching = false;
            })
            // The LATEST read wins (4 Oct 2026): switching branch while a read
            // is in flight must not leave the old branch's shelf up.
            .addCase(fetchHealthcareVendorDetails.pending, (state, action) => {
                state.detailsReadId = action.meta?.requestId || null;
                state.isFetching = true;
                // Clear the previous failure so a retry shows the loader
                // rather than the error card it was dispatched from.
                state.error = null;
            })
            .addCase(fetchHealthcareVendorDetails.fulfilled, (state, action) => {
                if (state.detailsReadId && action.meta?.requestId !== state.detailsReadId) return;
                const { vendorId, outletId, sections, vendor } = action.payload;
                state.details = { vendorId, outletId, sections, vendor };
                state.selectedVendor = vendor;
                state.isFetching = false;
                state.error = null;
            })
            .addCase(fetchHealthcareVendorDetails.rejected, (state, action) => {
                if (state.detailsReadId && action.meta?.requestId !== state.detailsReadId) return;
                state.error = action.payload || "Failed to fetch healthcare vendor";
                // Drop whatever was loaded so the storefront cannot show a
                // previously visited pharmacy underneath a failed load.
                state.details = null;
                state.selectedVendor = null;
                state.isFetching = false;
            });
    },
});

export const {
    resetOptionGroups,
    setSelectedVendor,
    applyBranchProductChange,
} = healthcareSlice.actions;
export default healthcareSlice.reducer;
